import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import { Select, List, Dropdown } from 'antd';
import { RefSelectProps } from 'antd/lib/select';
import Fuse from 'fuse.js';
import groupBy from 'lodash/groupBy';
import React, { FC, useEffect, useReducer, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Avatar } from 'components/atoms/avatar';
import { Menu } from 'components/atoms/dropdown';
import { FormGroup } from 'components/atoms/form-group';
import { Content } from './content/content';
import { Option, FollowersProps } from './followers.interface';
import styles from './followers.module.css';
import {
  convertSelectedValuesToApiPayload,
  constructSelectOptions,
  getSelectedValues,
} from './followers.utils';
import { NoContent } from './no-content';

const Followers: FC<FollowersProps> = (props) => {
  const ID = `users-dropdown-${new Date().getTime()}`;
  const DEFAULT = { users: [], teams: [] };
  const {
    options = { ...DEFAULT },
    value = { ...DEFAULT },
    onChange,
    canInviteUser = false,
    buttonIcon,
    dropdownHeaderLabel = <FormattedMessage id="task.follow" />,
    mode = 'button',
    size,
    label,
    placeholder,
    itemSelectedIcon = <CheckmarkOutline16 />,
    placement = 'bottomLeft',
    disabled = false,
    type,
    tooltipTitle,
    loading = false,
    attachTo,
  } = props;

  const [types, setTypes] = useState<string[]>([]);

  const selectRef = useRef<RefSelectProps>(null);

  const stringifyValue = JSON.stringify(value);

  const stringifyOptions = JSON.stringify(options);

  useEffect(() => {
    setTypes(Object.keys(JSON.parse(stringifyValue)));
  }, [stringifyValue]);

  /* TODO:: Check whether this options can be memoized */
  /* const selectOptionsMemoized = useCallback(
    () => constructSelectOptions(JSON.parse(stringifyOptions)),
    [stringifyOptions],
  ); */

  const selectOptions = constructSelectOptions(JSON.parse(stringifyOptions));
  const selectedValues = JSON.stringify(
    getSelectedValues(JSON.parse(stringifyValue)),
  );

  const [selected, setSelected] = useState<string[] | string>([]);

  useEffect(() => {
    setSelected(JSON.parse(selectedValues));
  }, [stringifyValue, selectedValues, setSelected]);

  const [searchText, setSearchText] = useState('');

  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  const fuseSearch = new Fuse(selectOptions, {
    distance: 3,
    threshold: 0.3,
    keys: ['name', 'description', 'email'],
  });

  const filterOptions = (str: string) =>
    fuseSearch.search(str).map((result) => result.item);

  const optionsToDisplayReducer = (
    state: Option[],
    action: { type: string; data?: string },
  ) => {
    switch (action.type) {
      case 'SEARCH':
        return action.data ? filterOptions(action.data) : selectOptions;
      case 'OPEN':
        return selectOptions;
      default:
        return selectOptions;
    }
  };

  const [optionsToDisplay, dispatch] = useReducer(
    optionsToDisplayReducer,
    selectOptions,
  );

  useEffect(() => {
    if (isDropdownVisible) {
      selectRef?.current?.focus();
      dispatch({ type: 'OPEN' });
    }
  }, [isDropdownVisible]);

  const handleSearch = (str: string) => {
    setSearchText(str);
    dispatch({ type: 'SEARCH', data: str });
  };

  const clearSearch = () => {
    dispatch({ type: 'SEARCH', data: '' });
    setSearchText('');
  };

  const onDropdownVisibleChange = (visible: boolean) => {
    setIsDropdownVisible(visible);
  };

  const toggleDropdownVisible = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const handleChange = (_selectedValues: string[] | string) => {
    setSelected(_selectedValues);
    clearSearch();
    onChange(convertSelectedValuesToApiPayload(_selectedValues, types));
    if (!Array.isArray(_selectedValues)) {
      toggleDropdownVisible();
    }
  };

  const handleOnBlur = () => {
    clearSearch();
  };

  const groupedOptions = (
    _options,
  ): Array<{ label: string; options: Option[] }> => {
    const groups = groupBy(_options, 'type');

    return Object.keys(groups).map((key) => ({
      label: key,
      options: groups[key],
    }));
  };

  const DropdownMenu = () => (
    <Menu id={ID} className={styles.container}>
      <Menu.Header className={styles.header}>{dropdownHeaderLabel}</Menu.Header>
      <Select
        open
        autoFocus
        ref={selectRef}
        listHeight={240}
        mode={type}
        className={styles.select}
        dropdownClassName={`${styles.dropdown} dropdown--no-shadow`}
        notFoundContent={
          canInviteUser ? <NoContent searchText={searchText} /> : undefined
        }
        optionFilterProp="key"
        filterOption={false}
        tagRender={() => <></>}
        value={selected}
        getPopupContainer={() => document.getElementById(ID) as HTMLElement}
        menuItemSelectedIcon={itemSelectedIcon}
        showSearch
        onChange={handleChange}
        onSearch={handleSearch}
        onBlur={handleOnBlur}
      >
        {groupedOptions(optionsToDisplay).map((opt) => (
          <Select.OptGroup label={opt.label} key={opt.label}>
            {opt.options.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                <List.Item className={styles.list__item}>
                  <List.Item.Meta
                    className={styles.content}
                    avatar={
                      <Avatar
                        name={option?.name}
                        src={option?.photoUrl}
                        companyLogoUrl={option?.companyLogo}
                      />
                    }
                    title={option?.name}
                    description={option?.description}
                  />
                </List.Item>
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </Select>
    </Menu>
  );
  const ref = React.createRef<HTMLSelectElement>();

  return (
    <FormGroup>
      {label && <span className={styles.label}>{label}</span>}
      <Dropdown
        getPopupContainer={attachTo ? () => attachTo : undefined}
        placement={placement}
        onVisibleChange={onDropdownVisibleChange}
        trigger={['click']}
        overlay={DropdownMenu}
        visible={isDropdownVisible}
        disabled={loading || disabled}
      >
        <Content
          loading={loading}
          disabled={disabled}
          ref={ref}
          placeholder={placeholder}
          mode={mode}
          options={selectOptions}
          selected={selected}
          buttonIcon={buttonIcon}
          size={size}
          onClick={toggleDropdownVisible}
          tooltipTitle={tooltipTitle}
        />
      </Dropdown>
    </FormGroup>
  );
};

export { Followers };
